import type { ExpenseDto, GroupDto } from "@caloteiros/shared";
import { formatCents } from "@caloteiros/shared";
import { useMemo } from "react";
import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { firstName, formatDayLabel } from "../../lib/format";
import { useExpenses } from "../../lib/queries";
import { IconChevron } from "../Icons";
import { Avatar, EmptyState, Skeleton } from "../ui";

/** Agrupa as despesas por dia para a lista ter respiro e contexto. */
function groupByDay(expenses: ExpenseDto[]): { label: string; items: ExpenseDto[] }[] {
  const groups: { label: string; items: ExpenseDto[] }[] = [];
  for (const expense of expenses) {
    const label = formatDayLabel(expense.occurredAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(expense);
    else groups.push({ label, items: [expense] });
  }
  return groups;
}

export function ExpensesTab({ group }: { group: GroupDto }): ReactElement {
  const expenses = useExpenses(group.id);

  const nameOf = useMemo(() => {
    const map = new Map(group.members.map((member) => [member.person.id, member.person]));
    return (personId: string) => map.get(personId);
  }, [group.members]);

  if (expenses.isLoading) return <Skeleton height={72} count={4} />;

  if (!expenses.data || expenses.data.length === 0) {
    return (
      <EmptyState
        emoji="🧾"
        title="Nenhuma despesa ainda"
        description="Lance a primeira e a divisão aparece na hora."
        action={
          <Link className="btn btn-primary" to={`/app/grupos/${group.id}/despesas/nova`}>
            Lançar despesa
          </Link>
        }
      />
    );
  }

  return (
    <div>
      {groupByDay(expenses.data).map((day) => (
        <section key={day.label}>
          <h3 className="expense-day">{day.label}</h3>
          <div className="card">
            {day.items.map((expense) => {
              const payer = expense.payers[0] ? nameOf(expense.payers[0].personId) : undefined;
              const myShare = group.myPersonId
                ? expense.shares.find((share) => share.chargedToPersonId === group.myPersonId)
                : undefined;

              return (
                <Link
                  key={expense.id}
                  className="list-item"
                  to={`/app/grupos/${group.id}/despesas/${expense.id}`}
                >
                  {payer ? (
                    <Avatar name={payer.displayName} color={payer.avatarColor} />
                  ) : null}
                  <span className="grow">
                    <span className="list-item-title">{expense.description}</span>
                    <span className="faint" style={{ display: "block" }}>
                      {payer ? `${firstName(payer.displayName)} pagou` : "Sem pagante"}
                      {expense.payers.length > 1 ? ` +${expense.payers.length - 1}` : ""}
                      {" · "}
                      {expense.shares.length} {expense.shares.length === 1 ? "pessoa" : "pessoas"}
                    </span>
                  </span>
                  <span style={{ textAlign: "right" }}>
                    <span className="numeric" style={{ fontWeight: 700, display: "block" }}>
                      {formatCents(expense.amountCents)}
                    </span>
                    {myShare ? (
                      <span className="faint numeric">sua parte {formatCents(myShare.amountCents)}</span>
                    ) : (
                      <span className="faint">você ficou de fora</span>
                    )}
                  </span>
                  <IconChevron width={16} height={16} style={{ color: "var(--ink-faint)" }} />
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
